import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';

import { MatSort, Sort } from '@angular/material/sort';

import { MatTableDataSource } from '@angular/material/table';

import { TranslocoService } from '@jsverse/transloco';

import { Subject, takeUntil } from 'rxjs';

import { DownloadAsDialogResult } from 'app/modules/collection/components/download-as-dialog/download-as-dialog.component';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import { DocumentService } from '../../../../providers/storage-api/document.service';
import { ProjectService } from '../../../../providers/storage-api/project.service';
import { ReportService } from '../../../../providers/storage-api/report.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';
import { LoggerService } from '../../../../services/logger.service';
import { SystemService } from '../../../../services/system.service';
import { WidgetLimiterService } from '../../../../services/widget-limiter.service';
import { FilterService, WidgetQueryOptions } from '../../../collection/services/filter.service';
import { DashboardViewComponent } from '../../../dashboard/pages/dashboard-view/dashboard-view.component';
import { AbstractTableWidgetComponent } from '../widget/abstract-table-widget.component';
import { WidgetComponent } from '../widget/widget.component';

import { WidgetChartComponent, WidgetChartOptions } from '../widget-chart/widget-chart.component';
import {
  TopicScoringData,
  TopicScoringReport,
} from '../../services/chart-report/chart-report-topic-scoring/chart-report-topic-scoring';
import { PptxReport } from '../../services/pptx.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

const LO_HI_SCALE = 5;

export interface TopicScoringWidgetOptions extends WidgetChartOptions {
  mostCommonItem: string;
  filterKey: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-topic-scoring',
  templateUrl: './widget-topic-scoring.component.html',
  styleUrls: ['./widget-topic-scoring.component.scss'],
  standalone: false,
})
export class WidgetTopicScoringComponent
  extends AbstractTableWidgetComponent<TopicScoringWidgetOptions>
  implements OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild(MatSort) set tableSort(content: MatSort) {
    if (content) {
      this.dataSource.sort = content;
    }
  }

  @ViewChild('mentionsChart') mentionsChart: WidgetChartComponent;

  public downloadFormats: string[] = ['pptx', 'image', 'csv'];
  public canFilter = true;
  public hasAnalyses = true;

  displayColumns = [];
  columnDefs = [];
  dataSource: MatTableDataSource<TopicScoringData>;
  sort: Sort;

  private report: TopicScoringReport;

  private columnRanges: {
    [key: string]: {
      minValue: number;
      maxValue: number;
      range: number;
    };
  } = {};

  private unsub$ = new Subject<void>();

  constructor(
    private reportService: ReportService,
    private logger: LoggerService,
    private comparisonService: ComparisonHandlerService,
    private translocoService: TranslocoService,
    protected filterService: FilterService,
    protected dialogService: DialogService,
    protected documentService: DocumentService,
    protected alertService: AlertService,
    protected widgetLimiterService: WidgetLimiterService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected dashboardViewComponent: DashboardViewComponent,
    protected widgetComponent: WidgetComponent,
    protected projectService: ProjectService,
    protected systemService: SystemService,
    protected timezoneService: TimezoneService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      filterService,
      dialogService,
      documentService,
      projectService,
      alertService,
      widgetLimiterService,
      interactiveSmartSummaryService,
      dashboardViewComponent,
      widgetComponent,
      systemService,
      accountService,
      authService,
      timezoneService,
      customFieldService,
      commentsHandlerService
    );
    this.showDownloadCsvDialog = false;
    this.sort = {
      active: 'f1',
      direction: 'desc',
    };
  }

  ngOnInit() {
    super.ngOnInit();
    super.updateFilterList();

    this.translocoService
      .selectTranslateObject('widgets.datatable')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => {
        this.columnDefs = [
          { id: TopicScoringReport.KEY, label: translations['topic'] },
          { id: 'f1', label: translations['f1'] },
          { id: 'toFind', label: translations['topic-total'] },
          { id: 'found', label: translations['topic-hits'] },
          { id: 'foundCorrectly', label: translations['correct'] },
          { id: 'incorrect', label: translations['incorrect'] },
          { id: 'missing', label: translations['missing'] },
        ];
        this.displayColumns = this.columnDefs.map((col) => col.id);
      });

    this.report = new TopicScoringReport(
      this.widget.payload,
      this.reportService,
      this.filterService,
      this.comparisonService,
      this.timezoneService
    );
    this.dataSource = new MatTableDataSource([]);
    this.refresh(false, true);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.widgetLoadController.abort('destroyed');
    this.unsub$.next();
    this.unsub$.complete();
  }

  public async loadData(initialLoad = false) {
    try {
      const widgetQueryOptions = this.widgetQueryOptions;

      // when widget performs its initial load in the 'Show mentions' state
      if (this.widget.payload.isDrilledDown) {
        widgetQueryOptions.filters = JSON.parse(JSON.stringify(widgetQueryOptions.filters));
        this.unsetMentionsDocumentFilter(widgetQueryOptions.filters);
      }

      const query = this.filterService.getQueryForDocuments(widgetQueryOptions, false, true);
      const report = await this.report.executeReportForTopicScoring(
        query,
        this.payload,
        this.widget.payload.mostCommonItem,
        this.widgetDataSource
      );
      this.filteredDocumentCount = this.report.lastDocumentCount;

      if (this.widgetLoadController.signal.aborted) {
        return;
      }

      this.dataSource.data = report;
      this.noData = !report.length;

      if (!this.noData) {
        report.forEach((item) => {
          Object.keys(item).forEach((col) => {
            if (this.columnRanges.hasOwnProperty(col)) {
              this.columnRanges[col].maxValue = Math.max(
                this.columnRanges[col].maxValue,
                item[col]
              );
              this.columnRanges[col].minValue = Math.min(
                this.columnRanges[col].minValue,
                item[col]
              );
            }
          });
        });

        Object.keys(this.columnRanges).forEach((col) => {
          this.columnRanges[col].range =
            (this.columnRanges[col].maxValue - this.columnRanges[col].minValue) / (LO_HI_SCALE * 2);
        });
      }

      this.isLoading = false;
      this.dataSourceNotFound = false;
    } catch (error) {
      this.logger.error('Error occured during fetching topic scoring data', error);

      if ((error.status = 404)) {
        this.dataSourceNotFound = true;
        this.isLoading = false;
        this.noData = false;
        this.dataSource.data = [];
      }
      return;
    } finally {
      this.loadingFinished.emit(initialLoad);
    }
  }

  async refresh(_reset = false, initialLoad = false, _refreshedDatasource?: WidgetDataSource) {
    super.refresh();

    this.loadingStarted.emit(initialLoad);

    await this.widgetLimiterService.execute(async () => {
      if (this.widgetLoadController.signal.aborted) {
        return;
      }

      this.displayColumns.slice(1).forEach(
        (col) =>
          (this.columnRanges[col] = {
            maxValue: 0,
            minValue: Number.MAX_SAFE_INTEGER,
            range: 0,
          })
      );

      await this.loadData(initialLoad);
    });
  }

  redraw() {
    if (this.mentionsChart) {
      this.mentionsChart.redraw();
    }
  }

  public getCellClass(column, value) {
    if (value === this.columnRanges[column].maxValue) {
      // max value could overflow, so give it max heat
      return `value-score-hi-${LO_HI_SCALE}`;
    }

    let degree =
      Math.floor((value - this.columnRanges[column].minValue) / this.columnRanges[column].range) +
      1;
    let bucket;

    if (degree > LO_HI_SCALE) {
      degree = Math.floor(degree / 2);
      bucket = 'hi';
    } else {
      degree = LO_HI_SCALE - degree + 1;
      bucket = 'lo';
    }

    return `value-score-${bucket}-${degree}`;
  }

  getCellMenuData(row, column, _extraData) {
    return {
      item: row[TopicScoringReport.KEY],
      row,
      column,
    };
  }

  public async hideItem(row) {
    return super.hideItem({
      name: row[TopicScoringReport.KEY],
      type: this.payload.analyticItem,
    });
  }

  public showDocuments(item, _row, column) {
    const widgetQueryOptions = this.widgetQueryOptions;
    widgetQueryOptions.filters = JSON.parse(JSON.stringify(widgetQueryOptions.filters));

    this.setDocumentFilter(widgetQueryOptions.filters, item, column);

    this.getDocuments(widgetQueryOptions);
  }

  public hasMentions(value) {
    if (this.widgetDataSource.type === 'analysis' && this.widgetDataSource.sourceVersion < 3) {
      return true;
    }

    if (this.widgetDataSource.type === 'group' && this.widgetDataSource.sourceVersion < 4) {
      return true;
    }

    return value && this.widgetDataSource.hasTopicMentions;
  }

  public showMentionsToolTip(value) {
    if (!this.hasMentions(value)) {
      return this.translocoService.translate('widgets.no-topic-mentions-in-dataset');
    } else if (!value) {
      return this.translocoService.translate('widgets.no-topic-mentions');
    }
  }

  showMentions(item) {
    this.widget.payload.drilledDownLocation = item;
    this.widget.payload.isDrilledDown = true;
    this.widget.payload.drilledDownOn = 'mentions';
    this.widget.payload.filterKey = 'topicScoringWidget';
    this.setDocumentFilter(this.widget.payload.filters, item, 'incorrect');

    this.filteredDocumentCount = undefined;
  }

  private setDocumentFilter(filters, item, column) {
    const pushFilterIfNotExists = (filterSet, filter) => {
      if (
        !filterSet.some((f) => f.analyticItem === filter.analyticItem && f.value === filter.value)
      ) {
        filterSet.push(filter);
      }
    };

    let topicFilter: { analyticItem: string; value: any; key: string };
    let metadataFilter: { analyticItem: string; value: any; key: string };

    // Intentional fall-through in cases
    switch (column) {
      case 'missing':
        topicFilter = {
          analyticItem: 'analyses.topics.title',
          value: item,
          key: this.widget.payload.filterKey,
        };

        pushFilterIfNotExists(filters.topics.not, topicFilter);
      /* eslint-disable no-fallthrough */
      case 'toFind':
        metadataFilter = {
          analyticItem: `metadata.${this.widget.payload.mostCommonItem}`,
          value: item,
          key: this.widget.payload.filterKey,
        };

        pushFilterIfNotExists(filters.metadata.and, metadataFilter);
        break;
      case 'incorrect':
        metadataFilter = {
          analyticItem: `metadata.${this.widget.payload.mostCommonItem}`,
          value: item,
          key: this.widget.payload.filterKey,
        };

        pushFilterIfNotExists(filters.metadata.not, metadataFilter);
      /* eslint-disable no-fallthrough */
      case 'found':
        topicFilter = {
          analyticItem: 'analyses.topics.title',
          value: item,
          key: this.widget.payload.filterKey,
        };

        pushFilterIfNotExists(filters.topics.and, topicFilter);
        break;
      case 'foundCorrectly':
        metadataFilter = {
          analyticItem: `metadata.${this.widget.payload.mostCommonItem}`,
          value: item,
          key: this.widget.payload.filterKey,
        };
        topicFilter = {
          analyticItem: 'analyses.topics.title',
          value: item,
          key: this.widget.payload.filterKey,
        };

        pushFilterIfNotExists(filters.metadata.and, metadataFilter);
        pushFilterIfNotExists(filters.topics.and, topicFilter);
        break;
    }
  }

  unsetMentionsDocumentFilter(filters) {
    filters.metadata.not = filters.metadata.not.filter((f) => {
      if (!this.widget.payload.filterKey) {
        return (
          f.analyticItem !== `metadata.${this.widget.payload.mostCommonItem}` ||
          f.value !== this.widget.payload.drilledDownLocation
        );
      }
      return f.key !== this.widget.payload.filterKey;
    });

    filters.topics.and = filters.topics.and.filter((f) => {
      if (!this.widget.payload.filterKey) {
        return (
          f.analyticItem !== 'analyses.topics.title' ||
          f.value !== this.widget.payload.drilledDownLocation
        );
      }
      return f.key !== this.widget.payload.filterKey;
    });

    this.filteredDocumentCount = undefined;
    this.loadingFinished.emit(false);
  }

  async executeReportForCsv(_result: DownloadAsDialogResult) {
    return this.dataSource.data;
  }

  prepareDataForCsv(reportData) {
    let report = this.columnDefs
      .slice(1)
      .reduce(
        (prev, curr) => prev + ',' + `"${Utils.escapeCsvString(curr.label)}"`,
        `"${this.columnDefs[0].label}"`
      );
    report += '\n';

    reportData.sort((r1, r2) => {
      const dir = this.sort.direction === 'asc' ? 1 : -1;

      if (this.sort.active === TopicScoringReport.KEY) {
        return r1[this.sort.active].localeCompare(r2[this.sort.active]) * dir;
      }

      return (r1[this.sort.active] - r2[this.sort.active]) * dir;
    });

    for (const row of reportData) {
      report += this.columnDefs
        .slice(1)
        .reduce(
          (prev, curr) => prev + ',' + row[curr.id],
          `"${Utils.escapeCsvString(row[TopicScoringReport.KEY])}"`
        );
      report += '\n';
    }

    return report;
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    if (this.widget.payload.isDrilledDown) {
      return this.mentionsChart.exportAsPptx(pptx);
    } else {
      const dataOptions = this.addTitleSlideToPptx(pptx);

      const colW = [6, 1, 1, 1, 1, 1, 1];
      const pptTable = {
        title: this.widget.title,
        rows: [
          this.columnDefs.map((column) => ({
            text: column.label,
            options: { bold: true, fontSize: 16 },
          })),
        ],
      };

      this.dataSource.data.forEach((row) => {
        const rowGroup = [];

        this.displayColumns.forEach((key) =>
          rowGroup.push({ text: key === 'f1' ? row.f1.toFixed(2) : row[key] })
        );

        pptTable.rows.push(rowGroup);
      });

      const tableWidth = 12; // in inches

      pptx.addTableSlide(pptTable as any, {
        colW,
        w: tableWidth,
        data: dataOptions,
        autoPage: true,
        lineWeight: -0.3,
        border: {
          color: 'black',
          pt: 2,
          type: 'solid',
        },
      });
    }
  }

  async exportAsImage(): Promise<any> {
    if (this.widget.payload.isDrilledDown) {
      return this.mentionsChart.exportAsImage();
    } else {
      return super.exportAsImage();
    }
  }

  getItemName(item) {
    return item[TopicScoringReport.KEY];
  }

  get defaultTitle() {
    return this.translocoService.translate('widgets.topic-scoring');
  }

  get key() {
    return TopicScoringReport.KEY;
  }

  public get widgetQueryOptions(): WidgetQueryOptions {
    const widgetQueryOptions = super.widgetQueryOptions;

    widgetQueryOptions.analyticItems = ['topics'];
    widgetQueryOptions.pointName = this.widget.payload.mostCommonItem;

    return widgetQueryOptions;
  }

  get analyticItemFieldDetails() {
    return this.fieldDetailsMap.get(this.widget.payload.mostCommonItem);
  }

  getCommentsForPoint(_point: any) {
    return [];
  }
}
